/**
 * Detects an active creator-attribution ("Created By") filter so the empty
 * state can explain itself instead of just saying "nothing found".
 *
 * `companies.created_by` / `contacts.created_by` are sparsely populated: the
 * column only exists from 2026-07-06, so everything created before that is
 * permanently NULL, and rows created by an automation, import, or sync have no
 * creator either. Filtering by a person therefore returns zero rows for two
 * incompatible reasons — "they created nothing" and "their work was never
 * attributed" — and the table rendered both identically.
 *
 * That is not a cosmetic problem: this surfaced because a commission-only rep
 * ran exactly this filter to evidence his own work for pay, saw an empty table,
 * and had no way to tell it was measuring the column's coverage rather than his
 * output. An empty result must never silently read as "you did no work".
 */

/** Field ids that carry creator attribution and are known to be sparse. */
const CREATED_BY_FIELD_IDS: ReadonlySet<string> = new Set(["created_by"]);

/** Minimal shape of an advanced filter — matches `AdvancedFilterGroupLike`. */
export interface AttributionFilterLike {
  readonly field: string;
}

/** True when `fieldId` is a creator-attribution column. */
export function isCreatedByField(fieldId: string): boolean {
  return CREATED_BY_FIELD_IDS.has(fieldId);
}

/** Narrows an unknown entry to something carrying a `field` string. */
function isAttributionFilterLike(value: unknown): value is AttributionFilterLike {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { field?: unknown }).field === "string"
  );
}

/** A filter value that is present rather than cleared. */
function isPopulatedValue(value: unknown): boolean {
  if (value === null || value === undefined || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

/**
 * True when a creator-attribution filter is currently applied, via either the
 * advanced-filter panel or a simple filter.
 *
 * Both arguments are `unknown` on purpose. The table context declares
 * `effectiveAdvancedFilters` as `Record<string, unknown>` while it is actually
 * an ARRAY at runtime (`filters.advanced || []`), so neither the declared type
 * nor the real one can be trusted on its own. We narrow at runtime instead of
 * asserting a shape that may be a lie — a wrong assertion here would silently
 * suppress the caveat on the exact report it exists to protect.
 *
 * A simple-filter entry counts only when it holds a value — an empty string,
 * empty array, null, or undefined is a cleared filter, not an active one.
 */
export function hasCreatedByFilter(
  advancedFilters: unknown,
  simpleFilters: unknown
): boolean {
  if (Array.isArray(advancedFilters)) {
    if (
      advancedFilters.some(
        (filter) => isAttributionFilterLike(filter) && isCreatedByField(filter.field)
      )
    ) {
      return true;
    }
  } else if (typeof advancedFilters === "object" && advancedFilters !== null) {
    // Defensive: honour the DECLARED keyed-record shape too, so this keeps
    // working if the runtime value is ever brought in line with the type.
    if (
      Object.entries(advancedFilters).some(
        ([fieldId, value]) => isCreatedByField(fieldId) && isPopulatedValue(value)
      )
    ) {
      return true;
    }
  }

  if (typeof simpleFilters !== "object" || simpleFilters === null) return false;

  return Object.entries(simpleFilters).some(
    ([fieldId, value]) => isCreatedByField(fieldId) && isPopulatedValue(value)
  );
}
